import React, { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { DIALOG_CLOSE_BUTTON_SX } from "../../../constants/dialogCloseButtonStyles";
import { withoutBackdropClose } from "../../../utils/withoutBackdropClose";
import {
  getAutoTagGroupLabel,
  sortAutoTagGroupsByLabel,
  type RegulationAutoTagGroup,
} from "../../../services/regulationAutoTags";

export type AutoTagsViewAllDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
  groups: RegulationAutoTagGroup[];
  /** Regulation ID shown under the modal title (AC2). */
  regulationId?: string | null;
  /** Regulation name/title shown under the modal title (AC2). */
  regulationTitle?: string | null;
}>;

const valueChipSx = {
  borderRadius: "16px",
  height: "27px",
  backgroundColor: "#f3f4f6",
  border: "none",
  color: "text.primary",
  fontSize: (theme: { typography: { body2: { fontSize: string } } }) =>
    theme.typography.body2.fontSize,
  "& .MuiChip-label": { px: 1 },
};

const highlightMatch = (text: string, term: string) => {
  if (!term.trim()) return text;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ color: "primary.main" }}>
        {text.slice(idx, idx + term.length)}
      </Box>
      {text.slice(idx + term.length)}
    </>
  );
};

export function AutoTagsViewAllDialog({
  open,
  onClose,
  groups,
  regulationId,
  regulationTitle,
}: AutoTagsViewAllDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const sections = useMemo(
    () =>
      sortAutoTagGroupsByLabel(
        groups.filter(
          (group) => Array.isArray(group.values) && group.values.length > 0,
        ),
      ),
    [groups],
  );

  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return sections;
    const term = searchTerm.toLowerCase();
    return sortAutoTagGroupsByLabel(
      sections
        .map((group) => {
          const filteredValues = group.values.filter(
            (value) =>
              value.name.toLowerCase().includes(term) ||
              value.code.toLowerCase().includes(term),
          );
          if (filteredValues.length === 0) return null;
          return { ...group, values: filteredValues };
        })
        .filter((group): group is RegulationAutoTagGroup => group !== null),
    );
  }, [sections, searchTerm]);

  const headerSubtitle = useMemo(() => {
    const id =
      typeof regulationId === "string" && regulationId.trim()
        ? regulationId.trim()
        : "";
    const name =
      typeof regulationTitle === "string" && regulationTitle.trim()
        ? regulationTitle.trim()
        : "";
    if (id && name) return `${id} ${name}`;
    return id || name || "";
  }, [regulationId, regulationTitle]);

  const handleClose = () => {
    setSearchTerm("");
    onClose();
  };

  const hasSearch = searchTerm.trim().length > 0;
  const showEmptySearchState =
    hasSearch && filteredSections.length === 0 && sections.length > 0;

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(() => handleClose())}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: "12px" } } }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: "26px",
          pt: "22px",
          pb: "14px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="div"
            sx={{
              fontWeight: 700,
              fontSize: "18px",
              color: "text.primary",
              lineHeight: 1.3,
            }}
          >
            Tags Details
          </Typography>
          {headerSubtitle ? (
            <Typography
              component="div"
              data-testid="tags-details-subtitle"
              sx={{
                fontSize: "12px",
                color: "#6A7282",
                mt: "4px",
                lineHeight: 1.4,
                wordBreak: "break-word",
              }}
            >
              {headerSubtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ ...DIALOG_CLOSE_BUTTON_SX, mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <Box sx={{ px: "26px", pt: 2, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search tags"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputProps={{ "aria-label": "Search tags" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#8C8C8C", fontSize: "1.1rem" }} />
              </InputAdornment>
            ),
            endAdornment: hasSearch ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearchTerm("")}
                  edge="end"
                >
                  <ClearIcon sx={{ fontSize: "1rem", color: "#99A1AF" }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "3px",
              fontSize: "13px",
              backgroundColor: "#fff",
              height: 36,
              "& fieldset": { borderColor: "#D9D9D9" },
              "&:hover fieldset": { borderColor: "#D9D9D9" },
              "&.Mui-focused fieldset": { borderColor: "#4166EB" },
            },
          }}
        />
      </Box>

      <DialogContent
        sx={{
          px: "26px",
          pt: "6px",
          pb: "22px",
          maxHeight: "600px",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#e5e7eb",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        }}
      >
        {showEmptySearchState ? (
          <Box
            data-testid="tags-details-empty"
            sx={{ py: 4, textAlign: "center" }}
          >
            <Typography sx={{ fontSize: "13px", color: "#6A7282" }}>
              No tags match
            </Typography>
          </Box>
        ) : filteredSections.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: "13px", color: "#6A7282" }}>
              No tags available.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {filteredSections.map((group, index) => {
              const label = getAutoTagGroupLabel(group);
              const count = group.values.length;

              return (
                <Box
                  key={`${group.concept}-${index}`}
                  data-testid="tags-details-group"
                  sx={{
                    py: "14px",
                    borderBottom:
                      index < filteredSections.length - 1
                        ? "1px solid #F1F2F4"
                        : "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#101828",
                      mb: "10px",
                    }}
                  >
                    {label} ({count})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {group.values.map((value) => (
                      <Chip
                        key={`${group.concept}-${value.code}-${value.name}`}
                        label={highlightMatch(value.name, searchTerm)}
                        sx={valueChipSx}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
